import { ancoraPorId, type ConfiguracaoSrs, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'

type ChaveSrs = keyof ConfiguracaoSrs

interface Modulo {
  chave: ChaveSrs
  posicao: [number, number, number]
  /** dimensão do módulo fechado */
  moduloArgs: [number, number, number]
  /** raio quando expandido */
  raioExpandido: number
  /** direção em que o airbag expande */
  direcaoExpansao: [number, number, number]
}

/**
 * Módulos SRS do perfil didático.
 *
 * Só aparece o que o perfil declara. A maquete é configurável exatamente para
 * não sugerir que todo veículo tenha todos os dispositivos, nem nestas posições.
 */
function modulos(perfil: PerfilVeiculo): Modulo[] {
  const motorista = ancoraPorId(perfil, 'motorista')
  const passageiro = ancoraPorId(perfil, 'passageiro')
  const zPainel = perfil.colunaA.zBase - 0.22
  const yPainel = perfil.cinturaY - 0.13
  const mx = perfil.largura / 2 - 0.1

  return [
    {
      chave: 'airbagMotorista',
      posicao: [motorista.posicao.x, yPainel - 0.01, perfil.colunaA.zBase - 0.36],
      moduloArgs: [0.14, 0.05, 0.14],
      raioExpandido: 0.19,
      direcaoExpansao: [0, 0.02, -0.16],
    },
    {
      chave: 'airbagPassageiro',
      posicao: [passageiro.posicao.x, yPainel, zPainel],
      moduloArgs: [0.2, 0.06, 0.12],
      raioExpandido: 0.2,
      direcaoExpansao: [0, 0.04, -0.18],
    },
    {
      chave: 'airbagLateralEsquerdo',
      posicao: [mx - 0.05, motorista.alturaAssento + 0.34, motorista.posicao.z - 0.14],
      moduloArgs: [0.07, 0.16, 0.1],
      raioExpandido: 0.15,
      direcaoExpansao: [-0.06, 0, 0.1],
    },
    {
      chave: 'airbagLateralDireito',
      posicao: [-mx + 0.05, passageiro.alturaAssento + 0.34, passageiro.posicao.z - 0.14],
      moduloArgs: [0.07, 0.16, 0.1],
      raioExpandido: 0.15,
      direcaoExpansao: [0.06, 0, 0.1],
    },
    {
      chave: 'airbagCortinaEsquerdo',
      posicao: [mx, perfil.tetoY - 0.08, (perfil.colunaA.zTopo + perfil.colunaC.zTopo) / 2],
      moduloArgs: [0.06, 0.06, perfil.colunaA.zTopo - perfil.colunaC.zTopo],
      raioExpandido: 0.13,
      direcaoExpansao: [0, -0.2, 0],
    },
    {
      chave: 'airbagCortinaDireito',
      posicao: [-mx, perfil.tetoY - 0.08, (perfil.colunaA.zTopo + perfil.colunaC.zTopo) / 2],
      moduloArgs: [0.06, 0.06, perfil.colunaA.zTopo - perfil.colunaC.zTopo],
      raioExpandido: 0.13,
      direcaoExpansao: [0, -0.2, 0],
    },
    {
      chave: 'airbagBancoMotorista',
      posicao: [motorista.posicao.x + 0.2, motorista.alturaAssento + 0.2, motorista.posicao.z - 0.18],
      moduloArgs: [0.06, 0.14, 0.08],
      raioExpandido: 0.12,
      direcaoExpansao: [-0.05, 0, 0.08],
    },
    {
      chave: 'airbagColunaMotorista',
      posicao: [perfil.largura / 2 - 0.1, perfil.cinturaY + 0.18, perfil.colunaB.z],
      moduloArgs: [0.07, 0.12, 0.07],
      raioExpandido: 0.12,
      direcaoExpansao: [-0.05, -0.06, 0.06],
    },
  ]
}

export function SistemaSRS({ perfil }: { perfil: PerfilVeiculo }) {
  const { mostrarRotulos, camadas, expansaoAirbag, srsAcionados } = useMaquete()
  const presentes = modulos(perfil).filter((m) => perfil.srs[m.chave])
  const zCentral = perfil.colunaA.zBase - 0.18
  const yCentral = perfil.pisoY + 0.08
  const mxLateral = perfil.largura / 2 - 0.06
  const mxDianteiro = perfil.largura * 0.28
  const yDianteiro = perfil.topoSoleiraY + 0.12
  const zDianteiro = perfil.frenteZ - 0.22

  return (
    <group>
      {presentes.map((modulo) => (
        <ModuloSrs
          key={modulo.chave}
          modulo={modulo}
          // só expande o que ESTE tipo de colisão aciona: o módulo continua
          // desenhado, fechado, para o aluno ver que ele existe e não disparou
          expansao={srsAcionados.includes(modulo.chave) ? expansaoAirbag : 0}
          mostrarRotulo={mostrarRotulos && camadas.srs}
        />
      ))}

      {/* Módulo / Central Eletrônica do SRS no túnel central */}
      <Peca id="centralSrs" deslocamentoAfastado={[0, 0.2, 0]}>
        <mesh position={[0, yCentral, zCentral]}>
          <boxGeometry args={[0.13, 0.045, 0.12]} />
          <MaterialPeca id="centralSrs" corBase="#d97706" metalico={0.65} rugosidade={0.35} />
        </mesh>
        {mostrarRotulos && camadas.srs && (
          <Rotulo3D posicao={[0, yCentral + 0.12, zCentral]} texto="CENTRAL SRS" variante="srs" fator={3.4} />
        )}
      </Peca>

      {/* Sensores de impacto dianteiros */}
      {[
        { sinal: 1, id: 'sensorImpactoDianteiroEsquerdo', rotulo: 'SENSOR D' },
        { sinal: -1, id: 'sensorImpactoDianteiroDireito', rotulo: 'SENSOR D' },
      ].map(({ sinal, id, rotulo }) => (
        <Peca key={id} id={id} deslocamentoAfastado={[0, 0, 0.35]}>
          <mesh position={[sinal * mxDianteiro, yDianteiro, zDianteiro]}>
            <boxGeometry args={[0.045, 0.04, 0.045]} />
            <MaterialPeca id={id} corBase="#eab308" metalico={0.5} rugosidade={0.4} />
          </mesh>
          {mostrarRotulos && camadas.srs && (
            <Rotulo3D posicao={[sinal * mxDianteiro, yDianteiro + 0.1, zDianteiro]} texto={rotulo} variante="srs" fator={3.0} />
          )}
        </Peca>
      ))}

      {/* Sensores de impacto lateral nas colunas B / soleiras */}
      {[
        { sinal: 1, id: 'sensorImpactoLateralEsquerdo' },
        { sinal: -1, id: 'sensorImpactoLateralDireito' },
      ].map(({ sinal, id }) => (
        <Peca key={id} id={id} deslocamentoAfastado={[sinal * 0.3, 0, 0]}>
          <mesh position={[sinal * mxLateral, perfil.topoSoleiraY + 0.06, perfil.colunaB.z]}>
            <boxGeometry args={[0.035, 0.05, 0.04]} />
            <MaterialPeca id={id} corBase="#eab308" metalico={0.5} rugosidade={0.4} />
          </mesh>
        </Peca>
      ))}
    </group>
  )
}

function ModuloSrs({
  modulo,
  expansao,
  mostrarRotulo,
}: {
  modulo: Modulo
  expansao: number
  mostrarRotulo: boolean
}) {
  const id = modulo.chave
  const [x, y, z] = modulo.posicao
  const inflado = expansao > 0.02
  const raio = modulo.raioExpandido * expansao
  const [dx, dy, dz] = modulo.direcaoExpansao

  return (
    <Peca id={id}>
      <mesh position={[x, y, z]}>
        <boxGeometry args={modulo.moduloArgs} />
        <MaterialPeca id={id} metalico={0.35} rugosidade={0.45} />
      </mesh>

      {/* bolsa expansível: forma simples, escala pela timeline */}
      {inflado && (
        <mesh position={[x + dx * expansao, y + dy * expansao, z + dz * expansao]}>
          <sphereGeometry args={[Math.max(raio, 0.01), 18, 14]} />
          <MaterialPeca id={id} metalico={0} rugosidade={0.75} />
        </mesh>
      )}

      {mostrarRotulo && <RotuloSrs id={id} posicao={[x, y + 0.16, z]} />}
    </Peca>
  )
}

function RotuloSrs({ id, posicao }: { id: string; posicao: [number, number, number] }) {
  const sigla = componenteDidatico(id)?.sigla
  if (!sigla) return null
  return <Rotulo3D posicao={posicao} texto={sigla} variante="srs" fator={3.6} />
}
